// src/pages/UserProfile.js
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import { apiFetch, getSelectedOrgId } from "../services/api";

/**
 * UserProfile
 * - Shows user info & password change
 * - Organization Settings (for org admins / superadmins)
 *   • Organization Timezone (IANA)
 *   • Auto accept interview requests (checkbox)
 *   • Generate Careers Code (iframe embed with copy-to-clipboard)
 */
export default function UserProfile() {
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [me, setMe] = useState(null);

  // Editable user fields
  const [user_name, setUserName] = useState("");
  const [user_email, setUserEmail] = useState("");

  // Password change
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  // Organization section
  const [org, setOrg] = useState(null);
  const [orgBusy, setOrgBusy] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [notificationEmail, setNotificationEmail] = useState("");

  // New fields
  const [orgTimezone, setOrgTimezone] = useState("");
  const [orgAutoAccept, setOrgAutoAccept] = useState(false);
  const [orgBaseUrl, setOrgBaseUrl] = useState("");

  // Careers embed code UI
  const [showEmbed, setShowEmbed] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedOrgId = getSelectedOrgId();

  const isSuperAdmin = useMemo(() => {
    const g = (user?.user_group_name || user?.group || "").toString().toLowerCase();
    return g === "superadmin";
  }, [user]);

  const canSeeOrgSettings = useMemo(() => {
    return !!selectedOrgId || isSuperAdmin;
  }, [selectedOrgId, isSuperAdmin]);

  const refreshMe = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const meRes = await apiFetch("/api/users/me");
      setMe(meRes || null);
      setUserName(meRes?.user_name || meRes?.username || "");
      setUserEmail(meRes?.user_email || meRes?.email || "");

      const orgId = selectedOrgId || meRes?.organization_id;
      if (orgId) {
        try {
          const o = await apiFetch(`/api/organizations/${orgId}`);
          setOrg(o || null);
          setLogoPreview(o?.logo_url || "");
          setOrgTimezone(o?.org_timezone || "");
          setOrgAutoAccept(!!(o?.auto_accept_interview_requests));
          setOrgBaseUrl(o?.org_base_url || "");
          setNotificationEmail(o?.notification_email || "");
        } catch (_) {
          setOrg(null);
        }
      }

    } catch (e) {
      setErr(e?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [canSeeOrgSettings, selectedOrgId]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // ------- Basic Profile Save (non-org) -------
  const saveProfile = async () => {
    try {
      // Get the auth token from localStorage or wherever it's stored
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          user_name,
          user_email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      alert("Profile saved.");
      await refreshMe();
    } catch (e2) {
      console.error("Save profile error:", e2);
      alert(e2?.message || "Failed to save profile");
    }
  };

  // ------- Password Change (USING NATIVE FETCH) -------
  const changePassword = async () => {
    if (!curPwd || !newPwd || !newPwd2) return alert("Please fill all password fields.");
    if (newPwd !== newPwd2) return alert("New passwords do not match.");
    if (newPwd.length < 8) return alert("New password must be at least 8 characters.");
    
    setPwdBusy(true);
    try {
      // Get the auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      const response = await fetch('/api/users/me/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          current_password: curPwd, 
          new_password: newPwd,
          confirm_new_password: newPwd2  // Backend requires this field!
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      alert("Password changed successfully!");
      setCurPwd(""); 
      setNewPwd(""); 
      setNewPwd2("");
    } catch (e2) {
      console.error("Change password error:", e2);
      alert(e2?.message || "Failed to change password");
    } finally {
      setPwdBusy(false);
    }
  };

  // ------- Organization Save (USING NATIVE FETCH) -------
  const saveOrgSettings = async () => {
    if (!org && !me?.organization_id) return alert("No organization available.");
    setOrgBusy(true);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Step 1: Save JSON settings
      const settingsResponse = await fetch('/api/organizations/me/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          notification_email: notificationEmail || null,
          org_timezone: orgTimezone || null,
          auto_accept_interview_requests: !!orgAutoAccept,
          org_base_url: orgBaseUrl || null,
        }),
      });

      if (!settingsResponse.ok) {
        const errorData = await settingsResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Settings save failed: HTTP ${settingsResponse.status}`);
      }

      // Step 2: Upload logo separately if a new file was selected
      if (logoFile instanceof File) {
        const fd = new FormData();
        fd.append("logo", logoFile);
        
        const logoResponse = await fetch('/api/organizations/me/logo', {
          method: 'POST',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
            // Don't set Content-Type - browser will set it with boundary
          },
          body: fd
        });

        if (!logoResponse.ok) {
          const errorData = await logoResponse.json().catch(() => ({}));
          throw new Error(errorData.error || `Logo upload failed: HTTP ${logoResponse.status}`);
        }
      }

      alert("Organization settings saved successfully!");
      setLogoFile(null); // Reset file input
      await refreshMe();
    } catch (e) {
      console.error("Save org settings error:", e);
      alert(e?.message || "Failed to save organization settings");
    } finally {
      setOrgBusy(false);
    }
  };

  // ------- Timezones list (IANA) -------
  const ianaTimezones = useMemo(() => {
    if (typeof Intl !== "undefined" && Intl.supportedValuesOf) {
      try { return Intl.supportedValuesOf("timeZone"); } catch {}
    }
    return [
      "UTC",
      "Africa/Cairo","Africa/Casablanca","Africa/Johannesburg","Africa/Nairobi",
      "America/Chicago","America/New_York","America/Denver","America/Los_Angeles",
      "America/Sao_Paulo","America/Mexico_City","America/Toronto",
      "Asia/Amman","Asia/Baghdad","Asia/Beirut","Asia/Dubai","Asia/Gaza","Asia/Jerusalem",
      "Asia/Karachi","Asia/Kolkata","Asia/Qatar","Asia/Riyadh","Asia/Singapore","Asia/Tehran",
      "Asia/Tokyo","Asia/Shanghai","Asia/Hong_Kong","Asia/Seoul",
      "Australia/Sydney","Australia/Melbourne","Australia/Perth",
      "Europe/Amsterdam","Europe/Athens","Europe/Berlin","Europe/London",
      "Europe/Istanbul","Europe/Kyiv","Europe/Madrid","Europe/Moscow","Europe/Paris","Europe/Rome"
    ];
  }, []);

  // ------- Careers Code -------
  const careersCode = useMemo(() => {
    const orgId = (org?.organization_id || org?.id || selectedOrgId || "").toString();
    const base = orgBaseUrl || org?.org_base_url || "";
    return (
`<p><!-- IntervuePro Careers Embedings --></p>
<div id="rizqira-careers-container">&nbsp;</div>
<p><script
  src="https://careers.rizqira.net/rizqira-embed.v1.js"
  data-org-id="${orgId}"
  data-api-root="https://careers.rizqira.net/api"
  data-tenant-origin="${base}"><br />
</script></p>`
    );
  }, [org, orgBaseUrl, selectedOrgId]);

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(careersCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      alert("Copy failed. Please select all and copy manually.");
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="p-8">Loading…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div>
        <Navbar />
        <div className="p-8 text-red-600">Error: {err}</div>
      </div>
    );
  }

  const headerLeft = `${me?.user_name || ""} (${me?.username || ""}) - ${me?.user_group_name || ""} Profile`;

  return (
    <div>
      <Navbar />
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-2xl font-semibold mb-6">{headerLeft}</h1>

        {/* Basic info */}
        <div className="bg-white rounded shadow p-4 mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Display Name</label>
              <input
                className="border p-2 rounded"
                type="text"
                value={user_name}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Email</label>
              <input
                className="border p-2 rounded"
                type="email"
                value={user_email}
                onChange={(e) => setUserEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
              onClick={saveProfile}
            >
              Save Profile
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="bg-white rounded shadow p-4 mb-8">
          <h2 className="text-lg font-semibold mb-4">Change Password</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Current Password</label>
              <input
                className="border p-2 rounded"
                type="password"
                value={curPwd}
                onChange={(e) => setCurPwd(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">New Password</label>
              <input
                className="border p-2 rounded"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Confirm New Password</label>
              <input
                className="border p-2 rounded"
                type="password"
                value={newPwd2}
                onChange={(e) => setNewPwd2(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:opacity-50"
              onClick={changePassword}
              disabled={pwdBusy}
            >
              {pwdBusy ? "Changing…" : "Change Password"}
            </button>
          </div>
        </div>

        {/* Organization Settings */}
        {org && (
          <div className="bg-white rounded shadow p-4 mb-8">
            <h2 className="text-lg font-semibold mb-4">Organization Settings</h2>

            {/* Timezone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Organization Timezone</label>
                <select
                  className="border p-2 rounded"
                  value={orgTimezone}
                  onChange={(e) => setOrgTimezone(e.target.value)}
                >
                  <option value="">— Select a timezone —</option>
                  {ianaTimezones.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              {/* Auto accept interviews */}
              <div className="flex items-center gap-2 mt-6 md:mt-0">
                <input
                  id="autoAccept"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!orgAutoAccept}
                  onChange={(e) => setOrgAutoAccept(e.target.checked)}
                />
                <label htmlFor="autoAccept" className="text-sm">
                  Auto accept interview requests
                </label>
              </div>
            </div>

            {/* Org Base URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Organization Base URL</label>
                <input
                  className="border p-2 rounded"
                  type="text"
                  value={orgBaseUrl}
                  onChange={(e) => setOrgBaseUrl(e.target.value)}
                  placeholder="https://your-company.example"
                />
              </div>
            </div>

            {/* Notification email + logo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Resume Submissions Notification Email (optional)</label>
                <input
                  className="border p-2 rounded"
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="notifications@example.com"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-600 mb-1">Organization Logo (image only, optional)</label>
                <input
                  className="border p-2 rounded"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setLogoFile(f);
                    if (f) {
                      const reader = new FileReader();
                      reader.onload = () => setLogoPreview(String(reader.result));
                      reader.readAsDataURL(f);
                    } else {
                      setLogoPreview("");
                    }
                  }}
                />
                {logoPreview && (
                  <div className="mt-2">
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="max-h-20 object-contain rounded"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Careers Code */}
            <div className="mt-6">
              <button
                className="bg-gray-800 text-white px-3 py-1 rounded hover:bg-black"
                onClick={() => setShowEmbed((s) => !s)}
              >
                Generate Careers Code
              </button>

              {showEmbed && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Copy this iFrame embed:</span>
                    <div className="flex items-center gap-3">
                      {copied && <span className="text-green-600 text-sm">Code was copied to clipboard</span>}
                      <button
                        className="px-2 py-1 text-sm rounded border"
                        onClick={copyEmbed}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="w-full border rounded p-2 text-xs font-mono"
                    rows={8}
                    readOnly
                    value={careersCode}
                  />
                </div>
              )}
            </div>

            {/* Save */}
            <div className="mt-6">
              <button
                className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:opacity-50"
                onClick={saveOrgSettings}
                disabled={orgBusy}
              >
                {orgBusy ? "Saving…" : "Save Organization Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}